/**
 * react-native-svg does not apply defs/<style>, CSS variables, or color-mix().
 * Expand themed kit templates to literal fill attributes before SvgXml.
 */

function parseRgb(hex: string): { r: number; g: number; b: number } {
  let h = hex.trim();
  if (!h.startsWith('#')) h = `#${h}`;
  if (h.length === 4) {
    return {
      r: parseInt(h[1] + h[1], 16),
      g: parseInt(h[2] + h[2], 16),
      b: parseInt(h[3] + h[3], 16),
    };
  }
  const r = parseInt(h.slice(1, 3), 16);
  const g = parseInt(h.slice(3, 5), 16);
  const b = parseInt(h.slice(5, 7), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) {
    return { r: 128, g: 128, b: 128 };
  }
  return { r, g, b };
}

function toHexRgb({ r, g, b }: { r: number; g: number; b: number }): string {
  const c = (n: number) => Math.round(Math.min(255, Math.max(0, n)));
  return `#${c(r).toString(16).padStart(2, '0')}${c(g).toString(16).padStart(2, '0')}${c(b).toString(16).padStart(2, '0')}`;
}

/** CSS color-mix(in srgb, A p%, B): p% of A plus (100-p)% of B. */
function mixSrgb(aHex: string, bHex: string, percentA: number): string {
  const a = parseRgb(aHex);
  const b = parseRgb(bHex);
  const t = percentA / 100;
  return toHexRgb({
    r: a.r * t + b.r * (1 - t),
    g: a.g * t + b.g * (1 - t),
    b: a.b * t + b.b * (1 - t),
  });
}

function expandPlaceholders(svg: string, primary: string, secondary: string): string {
  return svg.replace(/__KIT_PRIMARY__/g, primary).replace(/__KIT_SECONDARY__/g, secondary);
}

function expandColorMix(svg: string, primary: string, secondary: string): string {
  const re =
    /color-mix\(in srgb, var\(--shirt-(primary|secondary)\) (\d+)%, (white|black)\)/g;
  return svg.replace(re, (_, which: 'primary' | 'secondary', pct: string, target: string) => {
    const base = which === 'primary' ? primary : secondary;
    const other = target === 'white' ? '#ffffff' : '#000000';
    return mixSrgb(base, other, Number(pct));
  });
}

function expandVars(svg: string, primary: string, secondary: string): string {
  return svg
    .replace(/var\(--shirt-primary\)/g, primary)
    .replace(/var\(--shirt-secondary\)/g, secondary);
}

function collectClassFills(styleInner: string): Map<string, string> {
  const map = new Map<string, string>();
  const ruleRe = /\.([\w-]+)\s*\{\s*fill:\s*([^};]+);/g;
  let m: RegExpExecArray | null;
  while ((m = ruleRe.exec(styleInner)) !== null) {
    map.set(m[1], m[2].trim());
  }
  return map;
}

function stripDefsStyleBlock(svg: string): string {
  return svg.replace(/<defs>\s*<style>[\s\S]*?<\/style>\s*<\/defs>\s*/i, '');
}

/** Longer class names first so partial id collisions stay unlikely. */
function inlineFillsFromClasses(svg: string, fillByClass: Map<string, string>): string {
  const names = [...fillByClass.keys()].sort((a, b) => b.length - a.length);
  let out = svg;
  for (const cls of names) {
    const fill = fillByClass.get(cls);
    if (!fill) continue;
    const escaped = cls.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(
      `<(path|circle|rect|polygon|ellipse)(\\s+)class="${escaped}"`,
      'g',
    );
    out = out.replace(re, `<$1 fill="${fill}"$2class="${cls}"`);
  }
  return out;
}

function stripRootCssVarsStyle(svg: string): string {
  return svg.replace(/\sstyle="--shirt-primary:[^"]*"/, '');
}

/**
 * Full pipeline: placeholders → color-mix → var() → parse classes → inline fill → drop style defs.
 */
export function buildExpandedKitSvgXml(
  template: string,
  primaryHex: string,
  secondaryHex: string,
): string {
  let svg = expandPlaceholders(template, primaryHex, secondaryHex);
  svg = expandColorMix(svg, primaryHex, secondaryHex);
  svg = expandVars(svg, primaryHex, secondaryHex);
  const styleMatch = svg.match(/<style>([\s\S]*?)<\/style>/);
  if (!styleMatch) {
    return stripRootCssVarsStyle(svg);
  }
  const fillByClass = collectClassFills(styleMatch[1]);
  svg = stripDefsStyleBlock(svg);
  svg = inlineFillsFromClasses(svg, fillByClass);
  return stripRootCssVarsStyle(svg);
}
